import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor } from "@tylertech/logging";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria, ProfileSettingViewModel, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { ChartConfiguration, ChartEvent, LegendElement, LegendItem } from "chart.js";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

const enum ParameterName
{
    ClientVendorServiceType = "TotalMeterVsMeterReportingChartClientVendorServiceType"
}

const enum AdditionalChartOptions
{
    DefaultSelectionForClientVendorID = "DefaultSelectionForClientVendorID"
}

@Component({
    selector: "utilities-total-meter-vs-meter-reporting-chart",
    templateUrl: "./totalMeterReportingVsMeterReportingChart.tmpl.html",
    styleUrls: [
        "./totalMeterReportingVsMeterReportingChart.component.scss"
    ],
    standalone: false
})
export class TotalMeterReportingVsMeterReportingChartComponent implements OnInit, OnDestroy, ICustomViewComponent<ChartBindingObject>
{
    private readonly log = getLoggerFor("TotalMeterReportingVsMeterReportingChartComponent");
    private readonly destructor$ = new Subject<void>();
    private readonly serviceTypesSubject = new BehaviorSubject<Array<SettingLookupOption>>([]);
    private readonly _chartId: string;
    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;
    readonly customDataBinding: ChartBindingObject;
    readonly formGroup: FormGroup;

    private _chartCriteria: IChartCriteria;

    isLoaded: boolean;

    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartDialogService: ChartDialogService,
    )
    {
        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });

        this.formGroup = new FormGroup({
            serviceType: LgdFormControlFactory.select<SettingLookupOption>(
                undefined,
                false,
                {
                    displayValueFn: x => x.Display
                }),
        });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    private get serviceTypeControl(): AbstractControl<SettingLookupOption> { return this.formGroup.get("serviceType"); }
    private get selectedServiceType(): SettingLookupOption { return this.serviceTypeControl.value; }

    get serviceTypesObservable(): Observable<unknown[]> { return this.serviceTypesSubject.asObservable(); }

    get chartHasData(): boolean { return this.chartCriteria?.ChartDatas?.length > 0; }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        (this.chartHasData
            ? Promise.resolve()
            : this.loadChartData())
            .then(
                () =>
                {
                    const serviceTypes = this.chartCriteria.Parameters
                        .find(x => x.Id === ParameterName.ClientVendorServiceType)?.Options;
                    if (serviceTypes)
                    {
                        this.serviceTypesSubject.next(serviceTypes);
                        this.serviceTypeControl.setValue(serviceTypes[0], { emitEvent: false });
                    }

                    this.updateChartConfiguration();
                }, e =>
            {
                this.log.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        subscribeToValueChanges(this.serviceTypeControl, (_newValue: SettingLookupOption) =>
        {
            this.addServiceTypeParameterToCriteria();

            this.loadChartData();
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.chartHasData)
                    {
                        return;
                    }
                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.log.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private addServiceTypeParameterToCriteria(): void
    {
        let serviceTypeValue: ProfileSettingViewModel = this.chartCriteria?.Parameters
            ?.find(x => x.Id === ParameterName.ClientVendorServiceType);
        if (!serviceTypeValue)
        {
            serviceTypeValue = {
                Id: ParameterName.ClientVendorServiceType
            };
            this.chartCriteria?.Parameters?.push(serviceTypeValue);
        }

        serviceTypeValue.Value = this.selectedServiceType?.Value;
    }

    private updateChartConfiguration(): void
    {
        this.addServiceTypeParameterToCriteria();
        this.displayChart();

        const vendorServiceType = this.chartCriteria.Parameters
            .find(x => x.Id === ParameterName.ClientVendorServiceType);
        const defaultServiceTypeValue: string = vendorServiceType?.AdditionalAttributes
            ? vendorServiceType?.AdditionalAttributes[AdditionalChartOptions.DefaultSelectionForClientVendorID]
            : undefined;
        if (defaultServiceTypeValue && this.selectedServiceType?.Value !== defaultServiceTypeValue)
        {
            const serviceType = this.serviceTypesSubject.value.find(x => x.Value === defaultServiceTypeValue);
            this.serviceTypeControl.setValue(serviceType, { emitEvent: false });
        }
    }

    private displayChart(): void
    {
        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12, color: "#333333" },
                        onClick: (_event: ChartEvent, legendItem: LegendItem, legend: LegendElement<"bar">): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = !meta.hidden ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (!iMeta.hidden && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    }
                },
                elements: { line: {} },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    yAxes: {
                        type: "linear",
                        display: true,
                        position: "left",
                        // id: "y-axis-1",
                        grid: { display: false },
                        beginAtZero: true,
                        ticks: {
                            callback: (value: number) => value,
                        },
                    },
                },
            },
            data: {
                datasets: this.chartCriteria.ChartDatas.map((chartData, i) =>
                {
                    return {
                        type: "bar",
                        label: `${chartData.SetLabel}`,
                        data: chartData.Data,
                        fill: true,
                        backgroundColor: chartData.Additional[0],
                        borderColor: chartData.Additional[0],
                        pointBorderColor: this.chartCriteria.ChartDatas[0].Additional[0],
                        hoverBackgroundColor: chartData.Additional[0],
                        hoverBorderColor: chartData.Additional[0],
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    };
                })
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    displayChartParameterDialogAndReload(): void
    {
        const parameters = Utilities.deepCopy(this.chartCriteria.Parameters);
        this.$chartDialogService.show(parameters, "Total Meters vs Meters Reporting")
            .then((result: ProfileSettingViewModel[]) =>
            {
                if (Utilities.arraysEqual(this.chartCriteria.Parameters, result))
                {
                    this.log.debug(`Chart Parameters didn't change`);
                    return;
                }

                this.chartCriteria.Parameters = result;
                return this.loadChartData();
            });
    }
}
